import React, { useEffect, useRef, useState } from "react";
import Navbar from "./Navbar";
import { useLayout } from "../contexts/LayoutContext";
import ChatInterface from "../components/common/ReconChatAgent";
import Sidebar from "./sidebar/Sidebar";

interface LayoutProps {
  children: React.ReactNode;
  className?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, className = "" }) => {
  const {
    userName,
    customerName,
    showRightBar,
    pendingActionsData,
    notificationData,
    isOverlayOpen,
    setBusinessUnits,
    isCollapsed,
    chatInterface,
    chatbot,
    showNavbar,
    chatConfig,
    chatInitTriggerRef,
  } = useLayout();

  const chatInterfaceRef = useRef<any>(null);
  const chatConfigRef = useRef(chatConfig);
  chatConfigRef.current = chatConfig;
  const [initializingChat, setInitializingChat] = useState(false);

  const runInitChat = async () => {
    const config = chatConfigRef.current;
    if (!config.onInitializeChat || !chatInterfaceRef.current) return;
    setInitializingChat(true);
    const applyMessages = (messages: any[]) => {
      if (
        messages &&
        Array.isArray(messages) &&
        messages.length > 0 &&
        chatInterfaceRef.current
      ) {
        chatInterfaceRef.current.clearMessages();
        messages.forEach((message: any) => {
          if (chatInterfaceRef.current) {
            chatInterfaceRef.current.addMessage(message);
          }
        });
      }
    };
    try {
      const messages = await config.onInitializeChat({
        onProgress: applyMessages,
      });
      applyMessages(messages ?? []);
    } catch (error) {
      console.error("Error initializing chat:", error);
    } finally {
      setInitializingChat(false);
    }
  };

  // Run init when chat opens; expose trigger so pages can request re-init (e.g. when job metadata loads)
  useEffect(() => {
    if (
      chatInterface &&
      chatConfig.onInitializeChat &&
      chatInterfaceRef.current
    ) {
      runInitChat();
    }
    chatInitTriggerRef.current = runInitChat;
    return () => {
      chatInitTriggerRef.current = undefined;
    };
  }, [chatInterface, chatConfig.onInitializeChat]);

  return (
    <>
      {/* Main Layout Container */}
      <div
        className={`w-full relative rounded-boundvariablesdata bg-white-100 flex flex-row items-start justify-start flex-wrap content-start gap-0 leading-[normal] tracking-[normal] [row-gap:1.25rem] pb-16 md:pb-0 ${className}`}
      >
        {/* Sidebar - Fixed Position */}
        <div
          className={`fixed left-0 top-0 h-full ${
            isCollapsed ? "w-[4rem]" : "w-[15rem]"
          } bg-white z-50 md:hidden transition-all duration-300`}
        >
          <Sidebar userName={userName} isCollapsed={isCollapsed} />
        </div>

        {/* Main Content */}
        <section
          className={`flex-1 flex flex-col items-center justify-start gap-[19rem] min-w-[38.5rem] max-w-full ${
            showRightBar ? "mr-[18.75rem]" : ""
          } mq725:gap-[4.75rem] mq725:min-w-full mq975:gap-[9.5rem] scrollbar-hide md:w-[full] md:ml-0 md:mr-0 transition-all duration-300 ${
            chatInterface ? "mr-[30rem]" : ""
          } ${isCollapsed ? "ml-[4rem]" : "ml-[15rem]"}`}
        >
          <div className="self-stretch flex flex-col items-center justify-center gap-[0.6875rem] max-w-full mq725:gap-[1.6875rem] mq975:gap-[3.4375rem]">
            <div
              className={`w-full sticky top-0 z-[40]  transition-opacity duration-200 `}
            >
              {showNavbar && <Navbar />}
            </div>
            {children}
          </div>
        </section>

        {/* Chat Interface - Pass configuration from context */}
        {chatInterface && (
          <div className="fixed right-0 top-0 w-[30rem] h-full border-l border-solid border-[#EAECF0]">
            <ChatInterface
              ref={chatInterfaceRef}
              onSendMessage={chatConfig.onSendMessage}
              isLoading={(chatConfig.isLoading ?? false) || initializingChat}
              loadingMessage={chatConfig.loadingMessage}
              externalData={chatConfig.externalData}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Layout;
